/**
 * @description Pack SO Settings > Connectors (LYN-267 / LYN-374 / LYN-375)
 *
 * Key features:
 * - Visible only when the CP flag is on
 * - MFA note above the list and under the modal subtitle
 * - Login-only connect stores the next secret and assigns this company
 * - Select Connector assigns an existing secret. It does not copy the password
 * - Toggle off pauses the connection. Companies and history stay.
 *
 * @dependencies PackPage, soConnectorsApi, AuthContext
 *
 * @notes Login-only in the UI. No code step. No mail icon on password.
 *   Connect field is username or admin email (type=text).
 *   Label is the companies on the login. All requires a company first.
 *   LYN-373: latest ingest_runs.unmapped > 0 shows one company-mismatch line
 *   per unique unmatched Company cell. No email. No alias table.
 *   LYN-417: one notice at a time. Auth row wins, then Utilization catch-up,
 *   then mismatch. Catch-up is a list read. The button is the only pull.
 *   LYN-375: Download historical data opens from/to. Connect stays the
 *   current run. Connect and import is one click. After login verify,
 *   the modal closes when pull_status is importing. The row shows progress.
 *   Interrupted row Continue resumes remaining months, oldest first.
 *   LYN-379: import progress and failed-window retry live on the row.
 *   LYN-401: Select Connector returns after importing. The row poll owns
 *   replay progress. Do not raise the assign timeout.
 *   LYN-449: paused attach shows VERIFY_SPINNER and disables Connect/Cancel
 *   while the login verifies, same constant as Connect. A picked paused
 *   login is the only one listed, marked selected, with Change.
 */

import { useCallback, useEffect, useState } from 'react';
import { Check, Eye, EyeOff, Loader2, Lock, RefreshCw } from 'lucide-react';
import PackPage from '../../components/layouts/so/PackPage';
import ConfirmActionModal from '../../components/shared/ConfirmActionModal';
import { useAuth } from '../../contexts/AuthContext';
import {
  assignSoConnector,
  connectSoConnector,
  disconnectSoConnector,
  listSoConnectors,
  resumeSoConnector,
  testPullSoConnector,
  unassignSoConnector
} from '../../services/api/soConnectorsApi';

export const MFA_NOTE = 'Please connect with login credentials that do not require 2FA or MFA.';
export const VERIFY_SPINNER = 'Hang tight while we verify your login credentials.';
export const USERNAME_LABEL = 'Username or Admin Email';
export const SELECT_CONNECTOR = 'Select Connector';
export const REMOVE_FROM_CONNECTOR = 'Remove';
export const REMOVE_FROM_CONNECTOR_TITLE = 'Remove from connector';
export const REMOVE_FROM_CONNECTOR_CONFIRM = 'Remove';
export const REMOVE_FROM_CONNECTOR_CANCEL = 'Cancel';
export function removeFromConnectorMessage(companyName) {
  const name = String(companyName || '').trim() || 'this company';
  return `Remove ${name} from this connector? Its history is kept. To add it back, use Select Connector.`;
}
export const COMPANY_REQUIRED = 'Select a company to connect.';
export const HISTORICAL_TOGGLE = 'Download historical data';
export const CONNECT_AND_IMPORT = 'Connect and import';
export const IMPORT_IN_PROGRESS = 'Data import in progress...';
export const EXPORT_RANGE_FAILED = 'Could not export this date range. Try again.';
export const HISTORICAL_SPAN_CAP = 'Maximum download is 366 days.';
export const SYNC_INTERRUPTED = 'Sync interrupted. Continue the data import.';
export const CONTINUE_IMPORT = 'Continue';
export const IMPORT_IN_PROGRESS_PICKER = 'Import in progress';
export const RETRY_WINDOW = 'Retry';
export const UTILIZATION_CATCH_UP = 'Catch up';
const MAX_HISTORICAL_SPAN_DAYS = 366;
const IMPORT_POLL_MS = 4000;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Inclusive UTC days for the modal from/to. Over 366 is capped here.
 * @param {string} from
 * @param {string} to
 * @returns {number|null}
 */
function historicalSpanDays(from, to) {
  if (!from || !to) return null;
  const start = Date.parse(`${from}T00:00:00Z`);
  const end = Date.parse(`${to}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return null;
  return Math.round((end - start) / MS_PER_DAY) + 1;
}

/**
 * Close the modal once the row is importing. Do not wait on the live pull.
 * List poll is best-effort — a failed list must not trap the verify spinner.
 * @param {string} connectorId
 */
async function waitUntilRowImporting(connectorId) {
  for (let i = 0; i < 40; i += 1) {
    try {
      const res = await listSoConnectors();
      const found = (res.data || []).find((item) => item.id === connectorId);
      if (found?.pullStatus === 'importing') return;
    } catch (_error) {
      return;
    }
    await new Promise((resolve) => { setTimeout(resolve, 50); });
  }
}

export default function SoConnectorsPage() {
  const { activeCompanyId, companyScope, company, companies } = useAuth();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null);
  const [picker, setPicker] = useState(null);
  const [continuingId, setContinuingId] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removeError, setRemoveError] = useState(null);
  const [removing, setRemoving] = useState(false);

  const hasCompany = Boolean(activeCompanyId) && companyScope !== 'all';
  const activeCompanyName = company?.businessName
    || (companies || []).find((entry) => entry.id === activeCompanyId)?.businessName
    || 'this company';

  const reload = useCallback(async () => {
    const res = await listSoConnectors();
    setRows(res.data || []);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await listSoConnectors();
        if (!cancelled) setRows(res.data || []);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error?.message || 'Failed to load connectors');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [activeCompanyId]);

  const importing = (rows || []).some((row) => row.pullStatus === 'importing');
  useEffect(() => {
    if (!importing) return undefined;
    const timer = setInterval(() => {
      void reload();
    }, IMPORT_POLL_MS);
    return () => clearInterval(timer);
  }, [importing, reload]);

  const items = [
    { key: 'settings', label: 'Settings', to: '/so/settings' },
    { key: 'connectors', label: 'Connectors', to: '/so/settings/connectors' }
  ];

  return (
    <PackPage title="Connectors" subtitle="Source connections for this Sales Org." items={items}>
      <p className="text-sm text-gray-600 mb-4" data-testid="so-connectors-mfa-note">
        {MFA_NOTE}
      </p>
      {error ? <p className="text-sm text-red-600 mb-4">{error}</p> : null}
      {!hasCompany ? (
        <p className="text-sm text-gray-500 mb-4" data-testid="so-connectors-company-required">
          {COMPANY_REQUIRED}
        </p>
      ) : null}
      {!rows ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500">No connectors are flagged for this Sales Org.</p>
      ) : (
        <ul className="space-y-3" data-testid="so-connectors-list">
          {rows.map((row) => (
            <li key={row.id} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                  {row.logoUrl ? (
                    <img src={row.logoUrl} alt="" className="h-10 w-10 rounded-lg object-contain" />
                  ) : null}
                  <div>
                    <p className="text-sm font-medium text-gray-900">{row.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{row.description}</p>
                    <p className="text-xs text-gray-400 mt-2">
                      Last download {row.lastDownloadAt ? new Date(row.lastDownloadAt).toLocaleString() : '—'}
                    </p>
                    <p className="text-xs text-gray-400">
                      Last ingest {row.lastIngestAt ? new Date(row.lastIngestAt).toLocaleString() : '—'}
                    </p>
                    {row.pullStatus === 'importing' ? (
                      <p
                        className="text-xs text-gray-600 mt-2 inline-flex items-center gap-2"
                        data-testid="so-connector-row-import"
                      >
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        {IMPORT_IN_PROGRESS}
                      </p>
                    ) : null}
                    {row.pullStatus !== 'importing' && row.lastErrorMessage ? (
                      <p className="text-xs text-red-600 mt-2" data-testid="so-connector-row-error">
                        {row.lastErrorMessage}
                        {row.canRetry ? (
                          <button
                            type="button"
                            className="ml-1 align-middle text-gray-700 disabled:opacity-50"
                            aria-label={RETRY_WINDOW}
                            data-testid="so-connector-retry"
                            disabled={continuingId === row.id}
                            onClick={async () => {
                              setContinuingId(row.id);
                              try {
                                await testPullSoConnector(row.id, { retry: true });
                                await reload();
                              } finally {
                                setContinuingId(null);
                              }
                            }}
                          >
                            <RefreshCw className="h-3.5 w-3.5 inline" />
                          </button>
                        ) : null}
                      </p>
                    ) : null}
                    {row.pullStatus !== 'importing' && row.lastErrorMessage === SYNC_INTERRUPTED ? (
                      <button
                        type="button"
                        className="text-xs font-medium text-gray-900 mt-1 disabled:opacity-50"
                        data-testid="so-connector-continue"
                        disabled={continuingId === row.id}
                        onClick={async () => {
                          setContinuingId(row.id);
                          try {
                            await testPullSoConnector(row.id, { continue: true });
                            await reload();
                          } finally {
                            setContinuingId(null);
                          }
                        }}
                      >
                        {CONTINUE_IMPORT}
                      </button>
                    ) : null}
                    {row.pullStatus !== 'importing'
                      && row.lastErrorKind !== 'auth'
                      && row.lastErrorKind !== 'mfa'
                      && row.utilizationCatchUp
                      ? (
                        <p
                          className="text-xs text-amber-800 mt-2"
                          data-testid="so-connector-notice"
                        >
                          {row.utilizationCatchUp.message}
                          <button
                            type="button"
                            className="ml-2 align-middle text-xs font-medium text-gray-900 disabled:opacity-50"
                            data-testid="so-connector-utilization-catch-up"
                            disabled={continuingId === row.id}
                            onClick={async () => {
                              setContinuingId(row.id);
                              try {
                                await testPullSoConnector(row.id, { utilizationCatchUp: true });
                                await reload();
                              } finally {
                                setContinuingId(null);
                              }
                            }}
                          >
                            {UTILIZATION_CATCH_UP}
                          </button>
                        </p>
                      )
                      : null}
                    {row.pullStatus !== 'importing'
                      && row.lastErrorKind !== 'auth'
                      && row.lastErrorKind !== 'mfa'
                      && !row.utilizationCatchUp
                      ? (row.companyMismatchNotices || []).map((line) => (
                        <p
                          key={line}
                          className="text-xs text-amber-800 mt-2"
                          data-testid="so-connector-company-mismatch"
                        >
                          {line}
                        </p>
                      ))
                      : null}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  {row.connected ? (
                    <button
                      type="button"
                      aria-pressed={row.companyPaused !== true}
                      className={
                        row.companyPaused === true
                          ? 'relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 disabled:opacity-50'
                          : 'relative inline-flex h-6 w-11 items-center rounded-full bg-gray-900 disabled:opacity-50'
                      }
                      disabled={row.pullStatus === 'importing'}
                      data-testid={
                        row.companyPaused === true
                          ? 'so-connector-resume'
                          : 'so-connector-pause'
                      }
                      onClick={async () => {
                        if (row.companyPaused === true) {
                          await resumeSoConnector(row.id);
                        } else {
                          await disconnectSoConnector(row.id);
                        }
                        await reload();
                      }}
                    >
                      <span
                        className={
                          row.companyPaused === true
                            ? 'inline-block h-4 w-4 translate-x-1 rounded-full bg-white'
                            : 'inline-block h-4 w-4 translate-x-6 rounded-full bg-white'
                        }
                      />
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="text-sm font-medium text-white bg-gray-900 px-3 py-1.5 rounded-md disabled:opacity-50"
                        disabled={!hasCompany}
                        onClick={() => setModal({
                          ...row,
                          title: row.catalogTitle || row.title,
                          email: '',
                          password: '',
                          error: null,
                          verifying: false,
                          importing: false,
                          historical: false,
                          windowFrom: '',
                          windowTo: ''
                        })}
                      >
                        Connect
                      </button>
                      {!row.assigned && (row.selectableSecrets || []).length > 0 ? (
                        <button
                          type="button"
                          className="text-sm font-medium text-gray-900 border border-gray-200 px-3 py-1.5 rounded-md disabled:opacity-50"
                          disabled={!hasCompany || row.pullStatus === 'importing'}
                          data-testid="so-select-connector"
                          onClick={() => setPicker(row)}
                        >
                          {SELECT_CONNECTOR}
                        </button>
                      ) : null}
                    </>
                  )}
                  {row.assigned ? (
                    <button
                      type="button"
                      className="text-xs text-gray-600 text-right disabled:opacity-50"
                      disabled={!hasCompany || row.pullStatus === 'importing' || removing}
                      data-testid="so-remove-from-login"
                      onClick={() => {
                        setRemoveError(null);
                        setRemoveTarget(row);
                      }}
                    >
                      {REMOVE_FROM_CONNECTOR}
                    </button>
                  ) : null}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      {modal ? (
        <ConnectModal
          row={modal}
          onClose={async () => {
            setModal(null);
            await reload();
          }}
          onChange={setModal}
          onConnected={async () => {
            setModal(null);
            await reload();
          }}
        />
      ) : null}
      {picker ? (
        <SelectModal
          row={picker}
          onClose={() => setPicker(null)}
          onAssigned={async () => {
            setPicker(null);
            await reload();
          }}
        />
      ) : null}
      <ConfirmActionModal
        open={Boolean(removeTarget)}
        onClose={() => {
          if (removing) return;
          setRemoveTarget(null);
          setRemoveError(null);
        }}
        onConfirm={async () => {
          if (!removeTarget) return;
          setRemoving(true);
          setRemoveError(null);
          try {
            await unassignSoConnector(removeTarget.id);
            setRemoveTarget(null);
            await reload();
          } catch (err) {
            setRemoveError(
              err.response?.data?.error?.message || 'Could not remove this company.'
            );
            throw err;
          } finally {
            setRemoving(false);
          }
        }}
        title={REMOVE_FROM_CONNECTOR_TITLE}
        message={
          removeError
            ? `${removeFromConnectorMessage(activeCompanyName)} ${removeError}`
            : removeFromConnectorMessage(activeCompanyName)
        }
        confirmText={REMOVE_FROM_CONNECTOR_CONFIRM}
        cancelText={REMOVE_FROM_CONNECTOR_CANCEL}
        variant="danger"
        loading={removing}
      />
    </PackPage>
  );
}

function ConnectModal({ row, onClose, onChange, onConnected }) {
  const [showPassword, setShowPassword] = useState(false);
  const historical = row.historical === true;
  const busy = row.verifying === true;
  const missingWindow = historical && (!row.windowFrom || !row.windowTo);
  const reversedWindow = historical && row.windowFrom && row.windowTo
    && row.windowTo < row.windowFrom;
  const overCapWindow = historical && historicalSpanDays(row.windowFrom, row.windowTo)
    > MAX_HISTORICAL_SPAN_DAYS;

  const submit = async () => {
    if (reversedWindow) {
      onChange({ ...row, error: EXPORT_RANGE_FAILED });
      return;
    }
    if (overCapWindow) {
      onChange({ ...row, error: HISTORICAL_SPAN_CAP });
      return;
    }
    onChange({ ...row, verifying: true, error: null });
    try {
      await connectSoConnector(row.id, {
        email: row.email,
        password: row.password
      });
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Could not reach Towbook. Try again.';
      onChange({
        ...row,
        verifying: false,
        password: '',
        error: message
      });
      return;
    }
    const pull = historical
      ? testPullSoConnector(row.id, {
        from: row.windowFrom,
        to: row.windowTo
      })
      : testPullSoConnector(row.id);
    try {
      await Promise.race([
        waitUntilRowImporting(row.id),
        pull.catch(() => {})
      ]);
    } catch (_error) {
      // Connect already succeeded. Close even if the list poll fails.
    }
    try {
      await onConnected();
    } catch (_error) {
      // Row poll owns progress. Do not keep the verify spinner.
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md bg-white rounded-xl border border-gray-200 p-5" data-testid="so-connect-modal">
        <div className="flex items-center gap-3 mb-1">
          {row.logoUrl ? (
            <img src={row.logoUrl} alt="" className="h-8 w-8 rounded-md object-contain" />
          ) : null}
          <h2 className="text-lg font-semibold text-gray-900">{row.title}</h2>
        </div>
        <p className="text-xs text-gray-500">{row.description}</p>
        <>
            <label className="mt-2 flex items-center gap-2 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={historical}
                disabled={row.verifying}
                onChange={(event) => onChange({ ...row, historical: event.target.checked })}
                data-testid="so-connect-historical"
              />
              {HISTORICAL_TOGGLE}
            </label>
            {historical ? (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <label className="block text-xs text-gray-500">
                  From
                  <input
                    type="date"
                    disabled={row.verifying}
                    className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                    value={row.windowFrom || ''}
                    onChange={(event) => onChange({ ...row, windowFrom: event.target.value })}
                    data-testid="so-connect-from"
                  />
                </label>
                <label className="block text-xs text-gray-500">
                  To
                  <input
                    type="date"
                    disabled={row.verifying}
                    className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                    value={row.windowTo || ''}
                    onChange={(event) => onChange({ ...row, windowTo: event.target.value })}
                    data-testid="so-connect-to"
                  />
                </label>
              </div>
            ) : null}
            <p className="text-xs text-gray-600 mt-2" data-testid="so-connect-modal-mfa-note">
              {MFA_NOTE}
            </p>
            <label className="block text-xs text-gray-500 mt-4">
              {USERNAME_LABEL}
              <input
                type="text"
                autoComplete="username"
                disabled={row.verifying}
                className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                value={row.email}
                onChange={(event) => onChange({ ...row, email: event.target.value })}
                data-testid="so-connect-username"
              />
            </label>
            <label className="block text-xs text-gray-500 mt-3">
              Password
              <div className="mt-1 relative">
                <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  disabled={row.verifying}
                  className="w-full rounded-md border border-gray-200 pl-9 pr-10 py-2 text-sm"
                  value={row.password}
                  onChange={(event) => onChange({ ...row, password: event.target.value })}
                />
                <button
                  type="button"
                  className="absolute right-3 top-2.5 text-gray-400"
                  onClick={() => setShowPassword((value) => !value)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </label>
        </>
        {row.verifying ? (
          <p className="mt-4 text-sm text-gray-600 inline-flex items-center gap-2" data-testid="so-verify-spinner">
            <Loader2 className="h-4 w-4 animate-spin" />
            {VERIFY_SPINNER}
          </p>
        ) : null}
        {row.error || reversedWindow || overCapWindow ? (
          <p className="mt-3 text-sm text-red-600" data-testid="so-connect-error">
            {row.error || (overCapWindow ? HISTORICAL_SPAN_CAP : EXPORT_RANGE_FAILED)}
          </p>
        ) : null}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="text-sm text-gray-500 px-3 py-1.5" onClick={onClose} disabled={row.verifying}>
            Cancel
          </button>
          <button
            type="button"
            className="text-sm font-medium text-white bg-gray-900 px-3 py-1.5 rounded-md disabled:opacity-50"
            disabled={busy || !row.email || !row.password || missingWindow || reversedWindow || overCapWindow}
            onClick={submit}
          >
            {historical ? CONNECT_AND_IMPORT : 'Connect'}
          </button>
        </div>
      </div>
    </div>
  );
}

function isPausedSecret(secret) {
  return !(secret && secret.connected === true && secret.hasSecret === true);
}

function SelectModal({ row, onClose, onAssigned }) {
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [paused, setPaused] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const finishAssign = async (secret, credentials) => {
    setBusy(true);
    setError(null);
    try {
      if (credentials) {
        await assignSoConnector(row.id, secret.id, credentials);
      } else {
        await assignSoConnector(row.id, secret.id);
      }
    } catch (err) {
      setBusy(false);
      setError(err.response?.data?.error?.message || 'Could not assign connector');
      return;
    }
    if (credentials) {
      const pull = testPullSoConnector(row.id);
      try {
        await Promise.race([
          waitUntilRowImporting(row.id),
          pull.catch(() => {})
        ]);
      } catch (_error) {
        // Assign already succeeded. Close even if the list poll fails.
      }
    }
    try {
      await onAssigned();
    } catch (_error) {
      // Row poll owns progress.
    }
  };

  const choose = async (secret) => {
    if (isPausedSecret(secret)) {
      setPaused(secret);
      setError(null);
      return;
    }
    await finishAssign(secret);
  };

  const changePaused = () => {
    setPaused(null);
    setEmail('');
    setPassword('');
    setError(null);
  };

  const secrets = paused
    ? (row.selectableSecrets || []).filter((secret) => secret.id === paused.id)
    : (row.selectableSecrets || []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md bg-white rounded-xl border border-gray-200 p-5" data-testid="so-select-modal">
        <h2 className="text-lg font-semibold text-gray-900">{SELECT_CONNECTOR}</h2>
        <p className="text-xs text-gray-500 mt-1">
          Assign this company to an existing login. The password is not copied.
        </p>
        <ul className="mt-4 space-y-2">
          {secrets.map((secret) => {
            const selected = paused && secret.id === paused.id;
            const importing = secret.importing === true || secret.pullStatus === 'importing';
            return (
              <li key={secret.id}>
                {selected ? (
                  <div
                    className="flex items-center justify-between gap-2 text-sm font-medium text-gray-900 border border-gray-900 bg-gray-50 rounded-md px-3 py-2"
                    data-testid={`so-select-secret-${secret.labelOrdinal}`}
                  >
                    <span className="inline-flex items-center gap-2">
                      <Check className="h-4 w-4" data-testid="so-select-selected" />
                      {secret.label}
                    </span>
                    <button
                      type="button"
                      className="text-sm text-gray-700 underline"
                      data-testid="so-select-change"
                      disabled={busy}
                      onClick={changePaused}
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="w-full text-left text-sm font-medium text-gray-900 border border-gray-200 rounded-md px-3 py-2 disabled:opacity-50"
                    disabled={busy || importing}
                    data-testid={`so-select-secret-${secret.labelOrdinal}`}
                    onClick={() => choose(secret)}
                  >
                    {importing ? IMPORT_IN_PROGRESS_PICKER : secret.label}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
        {paused ? (
          <>
            <p className="text-xs text-gray-600 mt-4" data-testid="so-select-modal-mfa-note">
              {MFA_NOTE}
            </p>
            <label className="block text-xs text-gray-500 mt-4">
              {USERNAME_LABEL}
              <input
                type="text"
                autoComplete="username"
                disabled={busy}
                className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                data-testid="so-select-username"
              />
            </label>
            <label className="block text-xs text-gray-500 mt-3">
              Password
              <div className="mt-1 relative">
                <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  disabled={busy}
                  className="w-full rounded-md border border-gray-200 pl-9 pr-10 py-2 text-sm"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  data-testid="so-select-password"
                />
                <button
                  type="button"
                  className="absolute right-3 top-2.5 text-gray-400"
                  onClick={() => setShowPassword((value) => !value)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </label>
          </>
        ) : null}
        {paused && busy ? (
          <p className="mt-4 text-sm text-gray-600 inline-flex items-center gap-2" data-testid="so-verify-spinner">
            <Loader2 className="h-4 w-4 animate-spin" />
            {VERIFY_SPINNER}
          </p>
        ) : null}
        {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="text-sm text-gray-500 px-3 py-1.5" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          {paused ? (
            <button
              type="button"
              className="text-sm font-medium text-white bg-gray-900 px-3 py-1.5 rounded-md disabled:opacity-50"
              disabled={busy || !email || !password}
              data-testid="so-select-connect"
              onClick={() => finishAssign(paused, { email, password })}
            >
              Connect
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
